import {
  PageSection,
  Tabs,
  Tab,
  Toolbar,
  ToolbarItem,
  ToolbarContent,
  Button,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { ActiveForm } from '@app/components/form/active'
import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type { IEntry } from '@app/domain/instrument/instrument.interface'
import type {
  IDataInstrument,
  IVisitDefinition,
} from '@app/domain/visit/visit.interface'
import { theme } from '@app/utils/theme'

interface IVisitFormsProps {
  activeTabKey: number
  onActiveTabKeyChange: (tabKey: number) => void
  definition: IVisitDefinition
  entries?: IEntry[]
  newInstrumentAdded?: string
  openAddInstrumentModal: () => void
  openEditInstrumentModal: (entry: IEntry) => void
  visitId: string
}

const DownloadButtonWrapper = styled.span`
  .warning-icon {
    margin: 0 0.5em 0 0.5em;
    color: ${theme.colors.warning};
  }
  .download-button {
    vertical-align: 0.5em;
  }
`

export const VisitForms = ({
  activeTabKey,
  onActiveTabKeyChange,
  definition,
  entries,
  newInstrumentAdded,
  openAddInstrumentModal,
  openEditInstrumentModal,
  visitId,
}: IVisitFormsProps) => {
  const renderToolbar = useCallback(
    (form: IDataInstrument, entry: IEntry, formHasQuestions: boolean) => {
      return (
        <Toolbar id="form-actions">
          <ToolbarContent>
            <ToolbarItem>
              {formHasQuestions && entry ? (
                <DownloadButtonWrapper>
                  <Button
                    variant="secondary"
                    className="download-button"
                    onClick={() =>
                      openEditInstrumentModal(
                        entries
                          ? entries.find(
                              (entry) => entry.instrumentId === form.id,
                            )
                          : null,
                      )
                    }
                  >
                    Edit Responses
                  </Button>
                </DownloadButtonWrapper>
              ) : null}
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      )
    },
    [entries, openEditInstrumentModal],
  )

  const renderFormTab = useCallback(
    (instrument: IDataInstrument, key: number) => {
      const entry = entries?.find(
        (entry) => entry.instrumentId === instrument.id,
      )

      const formHasQuestions =
        instrument.questions && Object.keys(instrument.questions).length > 0

      if (newInstrumentAdded === instrument.id) {
        onActiveTabKeyChange(key)
      }

      return (
        <Tab key={key} eventKey={key} title={displayLabelIfExists(instrument)}>
          <PageSection isFilled={true}>
            {renderToolbar(instrument, entry, formHasQuestions)}
            {formHasQuestions ? (
              <ActiveForm
                key={`${instrument.id}-${instrument.version}`}
                form={instrument}
                visitId={visitId}
              />
            ) : (
              'This instrument does not have any questions configured.'
            )}
          </PageSection>
        </Tab>
      )
    },
    [entries, newInstrumentAdded, onActiveTabKeyChange, renderToolbar, visitId],
  )

  const handleTabClick = useCallback(
    (_, tabIndex) => {
      onActiveTabKeyChange(tabIndex)
    },
    [onActiveTabKeyChange],
  )

  const formTabs = useMemo(() => {
    const { allInstruments, allWorkflow } = definition
    const workflowForms = allWorkflow
      .map((workflow) =>
        allInstruments.filter((i) => i.id === workflow.instrumentId),
      )
      .filter((t) => t.length > 0)
      .map((it) => it[0])

    const addedForms = allInstruments.filter((instrument) => {
      return !workflowForms.includes(instrument)
    })

    const orderedForms = workflowForms.concat(addedForms)
    return orderedForms.map((t, idx) => renderFormTab(t, idx))
  }, [definition, renderFormTab])

  return (
    <Tabs
      activeKey={activeTabKey}
      onSelect={handleTabClick}
      onAdd={openAddInstrumentModal}
      addButtonAriaLabel="Add Instrument"
      isSecondary={true}
    >
      {formTabs}
    </Tabs>
  )
}
